import { FormInput } from "../../../../components/Forms";
import { Button, Flex, Icon, IconButton, Text, useBreakpointValue, useDisclosure, useToast } from "@chakra-ui/react";
import { MdAttachFile } from "react-icons/md";
import * as React from "react";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { showErrorToast, showSuccessToast } from "../../../../utils";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { FaDownload, FaTrash } from "react-icons/fa";
import { DeleteDialog } from "../../../DataEntryPlanning/components/DeleteDialog";

function FileAttachment({
                          type,
                          uploadHook,
                          downloadHook,
                          deleteHook,
                          invalidationKey,
                          currentAttachment,
                          isDisabled,
                          params = {},
                          tooltip = "",
                          onSuccessHandler = null
                        }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.fileAttachment"
  });
  const dispatch = useDispatch();
  const toast = useToast();
  const queryClient = useQueryClient();
  const {
    isOpen: isDeleteDialogOpen,
    onOpen: onDeleteDialogOpen,
    onClose: onDeleteDialogClose
  } = useDisclosure();

  const uploadBtnId = `${type}-file-attachment-btn`;

  const uploadBtnWidth = useBreakpointValue({
    base: "28",
    md: "32"
  });
  const btnFontSize = useBreakpointValue({
    base: "sm",
    md: "md"
  });
  const containerDisplay = useBreakpointValue({
    base: "block",
    md: "flex"
  });

  const getConfig = (onSuccessMessage = "") => {
    return {
      onMutate: () => dispatch(showAppSpinner()),
      onSuccess: (response) => {
        if (onSuccessMessage !== "") {
          showSuccessToast(toast, onSuccessMessage);
        }
        if (invalidationKey !== null) {
          queryClient.invalidateQueries(invalidationKey);
        }
        if (isDeleteDialogOpen) {
          onDeleteDialogClose();
        }
        if (onSuccessHandler) {
          onSuccessHandler(response);
        }
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      onSettled: () => dispatch(hideAppSpinner()),
      cacheTime: 0
    };
  };

  const { mutate: uploadFile } = uploadHook(getConfig(t("attachmentUploadedMsg")));
  const { mutate: deleteAttachment } = deleteHook(getConfig(t("attachmentRemovedMsg")));
  const { mutate: downloadAttachment } = downloadHook(getConfig());

  const handleDataImport = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      const formData = new FormData();
      formData.append("file", e.target.files[0]);
      document.getElementById(uploadBtnId).value = null;
      uploadFile({
        data: formData,
        ...params
      });
    }
  };

  const handleUploadFileClick = () => {
    const uploadBtn = document.getElementById(uploadBtnId);
    if (uploadBtn) {
      uploadBtn.click();
    }
  };

  const handleDownloadAttachment = () => {
    downloadAttachment({ attachmentId: currentAttachment?.attachmentId, ...params });
  };

  const removeAttachment = () => {
    deleteAttachment({ attachmentId: currentAttachment?.attachmentId, ...params });
  };

  return (
    <Flex alignItems="center" gap={2} display={containerDisplay}>
      <FormInput
        id={uploadBtnId}
        name={uploadBtnId}
        type="file"
        onChange={(e) => handleDataImport(e)}
        paddingInlineStart="0"
        border="none"
        accept={"image/*, .xlsx, .xls, .doc, .docx, .ppt, .pptx, .txt, .pdf"}
        controlProps={{ display: "none" }}
      />
      <Button
        colorScheme="white"
        color="esg_lightgreen"
        border={"1px solid"}
        borderColor={"esg_lightgreen"}
        width={uploadBtnWidth}
        fontSize={btnFontSize}
        onClick={!isDisabled ? handleUploadFileClick : () => {}}
        marginRight={3}
        title={tooltip !== "" ? tooltip : t("attachBtnTooltip")}
        isDisabled={isDisabled}
      >
        <Icon
          as={MdAttachFile}
          inlineSize="5"
          blockSize="5"
          color="esg_green"
          marginEnd="2"
          marginTop={containerDisplay === "block" ? "0" : "1"}
        />
        {t("title")}
      </Button>
      {currentAttachment && currentAttachment.attachmentId !== null && typeof currentAttachment.attachmentId !== "undefined" ?
        <Flex alignItems="center" gap={2} marginTop={containerDisplay === "block" ? "2" : "0"}>
          <Text maxWidth="150px" textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden"
                title={currentAttachment.attachmentName}>
            {currentAttachment.attachmentName}
          </Text>
          <IconButton
            aria-label="download-attachment-button"
            type="button"
            icon={<FaDownload size="18" color="green" />}
            flexShrink="0"
            backgroundColor="transparent"
            size="sm"
            opacity="0.7"
            tabIndex={-1}
            onClick={handleDownloadAttachment}
            title={t("downloadBtnTooltip")}
          />
          {!isDisabled ?
            <IconButton
              aria-label="remove-attachment-button"
              type="button"
              icon={<FaTrash size="18" color="red" />}
              flexShrink="0"
              backgroundColor="transparent"
              size="sm"
              opacity="0.6"
              tabIndex={-1}
              onClick={onDeleteDialogOpen}
              title={t("removeBtnTooltip")}
            /> : null
          }
        </Flex> : null
      }
      <DeleteDialog
        isOpen={isDeleteDialogOpen}
        onClose={onDeleteDialogClose}
        onDelete={removeAttachment}
        entityName={"Attachment"}
      />
    </Flex>
  );
}

export default FileAttachment;